import "components/apple-buttons/apple-sign-in_large--white.scss"

const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"
const SIGNUP_POPUP_FLAG = "signupPopupOpen"

let appleListenersAttached = false
let appleObserverStarted = false
let appleScriptPromise: Promise<void> | null = null

function isSignupPopupOpen() {
  return Boolean((window as any)[SIGNUP_POPUP_FLAG])
}

function dispatchSocialAuthSuccess(provider: string) {
  const event = new CustomEvent("social-auth:success", { detail: { provider } })
  window.dispatchEvent(event)
}

function loadAppleScript() {
  if ((window as any).AppleID) {
    return Promise.resolve()
  }

  if (appleScriptPromise) {
    return appleScriptPromise
  }

  appleScriptPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${APPLE_SCRIPT_SRC}"]`)
    if (existing) {
      if ((window as any).AppleID) {
        resolve()
      } else {
        existing.addEventListener("load", () => resolve())
      }
      return
    }

    const script = document.createElement("script")
    script.type = "text/javascript"
    script.src = APPLE_SCRIPT_SRC
    script.onload = () => resolve()
    document.head.appendChild(script)
  })

  return appleScriptPromise
}

function initAppleSignInForButton(appleButtonEl: HTMLElement) {
  const button = appleButtonEl
  const clientId = button.getAttribute("data-client-id")
  const redirectURI = button.getAttribute("data-redirect-uri")
  const state = button.getAttribute("data-state")
  const nonce = button.getAttribute("data-nonce")
  const appleAuth = (window as any).AppleID?.auth
  const appleContainer = button.parentElement || document.body

  if (!appleAuth) {
    return
  }

  const configKey = [clientId, redirectURI, state, nonce].join("|")
  if (button.dataset.appleConfig === configKey) {
    return
  }
  button.dataset.appleConfig = configKey

  const configureAuth = () => {
    appleAuth.init({
      clientId,
      scope: "name email",
      redirectURI,
      state,
      nonce,
      usePopup: true,
    })
  }

  configureAuth()

  if (!button.dataset.appleClickBound) {
    button.dataset.appleClickBound = "true"
    button.addEventListener("click", (event) => {
      event.preventDefault()
      configureAuth()
      if (typeof appleAuth.signIn === "function") {
        appleAuth.signIn()
        return
      }
      const scopedButton = appleContainer.querySelector("#appleid-signin")
      const appleSignInButton = scopedButton || document.querySelector("#appleid-signin")
      if (appleSignInButton instanceof HTMLElement) {
        appleSignInButton.click()
      }
    })
  }
}

function setupAppleSignIn() {
  const appleButtons = Array.from(document.querySelectorAll("#custom-apple-signin"))
  if (!appleButtons.length) {
    return
  }

  if (!appleListenersAttached) {
    appleListenersAttached = true
    document.addEventListener("AppleIDSignInOnSuccess", (event) => {
      fetch("/auth/apple/handle_success", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({ data: (event as any).detail }),
      })
        .then((response) => {
          if (response.ok) {
            if (isSignupPopupOpen()) {
              dispatchSocialAuthSuccess("apple")
            } else {
              window.location.href = "/profile"
            }
          } else {
            response.json().then((data) => {
              // eslint-disable-next-line no-alert
              alert(`Sign in with Apple failed: ${data.message || "Please try again."}`)
            })
          }
        })
        .catch(() => {
          // eslint-disable-next-line no-alert
          alert("An error occurred during sign in. Please try again.")
        })
    })

    document.addEventListener("AppleIDSignInOnFailure", (event) => {
      const error = (event as any).detail.error || "Unknown error"
      // eslint-disable-next-line no-alert
      alert(`Sign in with Apple failed: ${error}`)
    })
  }

  loadAppleScript().then(() => {
    appleButtons.forEach((button) => initAppleSignInForButton(button as HTMLElement))
  })
}

function observeAppleButtons() {
  if (!appleObserverStarted) {
    appleObserverStarted = true
    const observer = new MutationObserver(() => {
      setupAppleSignIn()
    })
    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  setupAppleSignIn()
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", observeAppleButtons)
} else {
  observeAppleButtons()
}
